<template>
    <section class="dp-layout">
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" pane-title="家校通讯录" @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>
        <section class="dp-right">
            <toolbar-filter-bar show-query @query="handleQuery" @submit="handleQuery">
                <template slot="fields">
                    <el-form-item>
                        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd"
                            start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false"
                            :picker-options="datePickerOptions"
                            class="toolbar-field-w-daterange"></el-date-picker>
                    </el-form-item>
                    <el-form-item v-if="courseSource === 'Special'">
                        <el-select v-model="filters.specialCourseTimeId" placeholder="特殊课时间" clearable>
                            <el-option v-for="t in specialTimes" :key="t.Id" :label="formatSpecialTimeLabel(t)" :value="t.Id"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item>
                        <el-select v-model="filters.status" placeholder="状态" clearable @change="handleQuery">
                            <el-option label="已评（准时+补评）" value="Reviewed"></el-option>
                            <el-option label="未评（待评+待补评）" value="Unreviewed"></el-option>
                            <el-option label="待点评" value="Pending"></el-option>
                            <el-option label="待补评" value="WaitingMakeup"></el-option>
                            <el-option label="准时已评" value="ReviewedOnTime"></el-option>
                            <el-option label="补评" value="ReviewedMakeup"></el-option>
                            <el-option label="已过期" value="Expired"></el-option>
                            <el-option label="未到开放" value="Upcoming"></el-option>
                            <el-option label="历史已评" value="History"></el-option>
                        </el-select>
                    </el-form-item>
                </template>
                <template slot="actions">
                    <el-button size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">导出</el-button>
                </template>
            </toolbar-filter-bar>

            <el-tabs v-model="activeTab" @tab-click="onTabClick">
                <el-tab-pane label="点评明细" name="records">
                    <div class="stat-summary" v-loading="listLoading">
                        <div
                            class="stat-summary-item is-clickable is-tone-due"
                            :class="{ 'is-active': filters.status === '' || !filters.status }"
                            @click="filterByStat('')"
                        >
                            <span class="stat-summary-label">应评</span>
                            <span class="stat-summary-value">{{ summary.DueCount || 0 }}</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-done"
                            :class="{ 'is-active': filters.status === 'Reviewed' }"
                            @click="filterByStat('Reviewed')"
                        >
                            <span class="stat-summary-label">已评</span>
                            <span class="stat-summary-value">{{ summary.ReviewedCount || 0 }}</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-ontime"
                            :class="{ 'is-active': filters.status === 'ReviewedOnTime' }"
                            @click="filterByStat('ReviewedOnTime')"
                        >
                            <span class="stat-summary-label">准时</span>
                            <span class="stat-summary-value">{{ summary.OnTimeCount || 0 }}</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-makeup"
                            :class="{ 'is-active': filters.status === 'ReviewedMakeup' }"
                            @click="filterByStat('ReviewedMakeup')"
                        >
                            <span class="stat-summary-label">补评</span>
                            <span class="stat-summary-value">{{ summary.MakeupCount || 0 }}</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-miss"
                            :class="{ 'is-active': filters.status === 'Unreviewed' }"
                            @click="filterByStat('Unreviewed')"
                        >
                            <span class="stat-summary-label">未评</span>
                            <span class="stat-summary-value">{{ summary.UnreviewedCount || 0 }}</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-expired"
                            :class="{ 'is-active': filters.status === 'Expired' }"
                            @click="filterByStat('Expired')"
                        >
                            <span class="stat-summary-label">过期</span>
                            <span class="stat-summary-value">{{ summary.ExpiredCount || 0 }}</span>
                        </div>
                        <div class="stat-summary-item is-tone-rate">
                            <span class="stat-summary-label">点评率</span>
                            <span class="stat-summary-value">{{ summary.ReviewRate || 0 }}%</span>
                        </div>
                        <div
                            class="stat-summary-item is-clickable is-tone-history"
                            :class="{ 'is-active': filters.status === 'History' }"
                            @click="filterByStat('History')"
                        >
                            <span class="stat-summary-label">历史已评</span>
                            <span class="stat-summary-value">{{ summary.HistoryReviewedCount || 0 }}</span>
                        </div>
                    </div>
                    <div class="tbl-scroll-host">
                        <el-table :data="rows" v-loading="listLoading" height="100%" border>
                            <el-table-column label="上课" min-width="150">
                                <template slot-scope="scope">
                                    <div class="cq-cell-stack">
                                        <strong>{{ formatDate(scope.row.AttendDate) }}</strong>
                                        <em>{{ scope.row.PeriodName || '-' }}<template v-if="scope.row.TimeRange"> · {{ scope.row.TimeRange }}</template></em>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column prop="ClassName" label="班级" min-width="120" show-overflow-tooltip></el-table-column>
                            <el-table-column prop="CourseName" label="课程" min-width="110" show-overflow-tooltip></el-table-column>
                            <el-table-column label="老师" min-width="120">
                                <template slot-scope="scope">
                                    <div class="cq-cell-stack">
                                        <span>任教 {{ scope.row.TeacherName || '-' }}</span>
                                        <em>点评 {{ scope.row.ReviewerTeacherName || '-' }}</em>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column label="状态" min-width="140" align="center">
                                <template slot-scope="scope">
                                    <div class="cq-cell-stack cq-cell-stack--center">
                                        <div class="cq-status-tags">
                                            <span v-if="scope.row.IsHistory" class="cq-status-chip is-history">历史</span>
                                            <span class="cq-status-chip" :class="statusChipClass(scope.row.Status)">
                                                {{ formatStatusText(scope.row.StatusText, scope.row.Status) }}
                                            </span>
                                        </div>
                                        <em v-if="scope.row.FirstSubmitTime">{{ formatDateTime(scope.row.FirstSubmitTime) }}</em>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column label="结果" min-width="130" align="center">
                                <template slot-scope="scope">
                                    <div class="cq-cell-stack cq-cell-stack--center" v-if="scope.row.Grade || scope.row.ReviewId">
                                        <span
                                            v-if="scope.row.Grade"
                                            class="cq-result-pill"
                                            :class="gradeTone(scope.row.Grade)"
                                        >
                                            {{ scope.row.Grade }}
                                            <i>扣{{ formatDeduct(scope.row.DeductScore) }}</i>
                                        </span>
                                        <strong v-else>-</strong>
                                        <em v-if="scope.row.ScoreRate != null && scope.row.ScoreRate !== ''">得分率 {{ formatRate(scope.row.ScoreRate) }}</em>
                                    </div>
                                    <span v-else class="muted">-</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="140" align="center" fixed="right">
                                <template slot-scope="scope">
                                    <div v-if="scope.row.TaskId && scope.row.ReviewId" class="table-op-pair">
                                        <el-button type="text" size="mini" class="table-op-link" @click="handleView(scope.row)">详情</el-button>
                                        <el-button type="text" size="mini" class="table-op-link table-op-link--danger" @click="handleDel(scope.row)">删除</el-button>
                                    </div>
                                    <el-button
                                        v-else-if="scope.row.TaskId"
                                        type="text"
                                        size="mini"
                                        class="table-op-link"
                                        @click="handleView(scope.row)"
                                    >详情</el-button>
                                    <span v-else class="muted">未提交</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                    <div class="pagination-container">
                        <el-pagination layout="total,prev, pager, next" :current-page="page" :page-size="pageSize"
                            :total="total" @current-change="handleCurrentChange" style="float: right"></el-pagination>
                    </div>
                </el-tab-pane>
                <el-tab-pane label="扣分汇总" name="deduct">
                    <div class="tbl-scroll-host">
                        <el-table :data="deductSummary" v-loading="deductLoading" height="100%" border class="cq-deduct-table">
                            <el-table-column type="index" label="#" width="60" align="center"></el-table-column>
                            <el-table-column prop="ClassName" label="班级" min-width="160" show-overflow-tooltip></el-table-column>
                            <el-table-column prop="HeadTeacherName" label="班主任" min-width="120" show-overflow-tooltip>
                                <template slot-scope="scope">{{ scope.row.HeadTeacherName || '-' }}</template>
                            </el-table-column>
                            <el-table-column label="应评" width="90" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-due">{{ scope.row.DueCount || 0 }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="已评" width="90" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-done">{{ scope.row.ReviewedCount || 0 }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="未评" width="90" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-miss" :class="{ 'is-zero': !(scope.row.UnreviewedCount > 0) }">
                                        {{ scope.row.UnreviewedCount || 0 }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column label="已评扣分" width="110" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-deduct-soft">{{ formatDeduct(scope.row.ReviewedDeductScore) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="未评扣分" width="110" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-deduct-warn">{{ formatDeduct(scope.row.UnreviewedDeductScore) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="总扣分" width="110" align="center">
                                <template slot-scope="scope">
                                    <span class="cq-num is-deduct-total">{{ formatDeduct(scope.row.TotalDeductScore) }}</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>
                <el-tab-pane label="班级排行" name="stats">
                    <div class="stat-summary" v-loading="rankLoading">
                        <div class="stat-summary-item is-tone-due"><span class="stat-summary-label">应评</span><span class="stat-summary-value">{{ summary.DueCount || 0 }}</span></div>
                        <div class="stat-summary-item is-tone-done"><span class="stat-summary-label">已评</span><span class="stat-summary-value">{{ summary.ReviewedCount || 0 }}</span></div>
                        <div class="stat-summary-item is-tone-rate"><span class="stat-summary-label">点评率</span><span class="stat-summary-value">{{ summary.ReviewRate || 0 }}%</span></div>
                        <div class="stat-summary-item is-tone-makeup"><span class="stat-summary-label">补评率</span><span class="stat-summary-value">{{ summary.MakeupRate || 0 }}%</span></div>
                    </div>
                    <div class="tbl-scroll-host">
                        <el-table :data="ranking" v-loading="rankLoading" height="100%" border>
                            <el-table-column type="index" label="#" min-width="70" align="center"></el-table-column>
                            <el-table-column prop="ClassName" label="班级" min-width="160" show-overflow-tooltip></el-table-column>
                            <el-table-column prop="HeadTeacherName" label="班主任" min-width="120" show-overflow-tooltip>
                                <template slot-scope="scope">{{ scope.row.HeadTeacherName || '-' }}</template>
                            </el-table-column>
                            <el-table-column prop="SampleCount" label="有效课堂" min-width="110" align="center"></el-table-column>
                            <el-table-column prop="DueCount" label="应评" min-width="90" align="center"></el-table-column>
                            <el-table-column prop="ReviewedCount" label="已评" min-width="90" align="center"></el-table-column>
                            <el-table-column label="覆盖率" min-width="100" align="center">
                                <template slot-scope="scope">{{ formatRate(scope.row.CoverageRate) }}</template>
                            </el-table-column>
                            <el-table-column label="官方得分率" min-width="120" align="center">
                                <template slot-scope="scope">
                                    <span v-if="scope.row.SampleInsufficient" class="muted">样本不足</span>
                                    <span v-else>{{ formatRate(scope.row.ScoreRate) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="总扣分" min-width="100" align="center">
                                <template slot-scope="scope">{{ formatDeduct(scope.row.TotalDeductScore) }}</template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <el-dialog
                title="点评详情"
                :visible.sync="detailVisible"
                width="780px"
                custom-class="cq-detail-dialog"
                :close-on-click-modal="false"
                @closed="onDetailClosed"
            >
                <div class="cq-detail-body" v-loading="detailLoading">
                    <template v-if="detail">
                        <header class="cqd-head">
                            <div class="cqd-head__copy">
                                <p class="cqd-kicker">{{ formatDate(detail.Session && detail.Session.AttendDate) }}</p>
                                <h3>
                                    {{ (detail.Session && detail.Session.ClassName) || '—' }}
                                    <em class="cqd-time" v-if="detailSessionTime">{{ detailSessionTime }}</em>
                                </h3>
                                <p class="cqd-sub">
                                    <span>{{ (detail.Session && detail.Session.CourseName) || '—' }}</span>
                                    <span v-if="detail.Session && detail.Session.PeriodName">{{ detail.Session.PeriodName }}</span>
                                </p>
                            </div>
                            <span
                                class="cq-status-chip"
                                :class="statusChipClass(detail.Session && detail.Session.Status)"
                            >
                                {{ formatStatusText(detail.Session && detail.Session.StatusText, detail.Session && detail.Session.Status) }}
                            </span>
                        </header>

                        <div class="cqd-meta">
                            <div class="cqd-meta__cell">
                                <em>任教老师</em>
                                <strong>{{ (detail.Session && detail.Session.TeacherName) || '—' }}</strong>
                            </div>
                            <div class="cqd-meta__cell">
                                <em>点评老师</em>
                                <strong>{{ detail.ReviewerTeacherName || '—' }}</strong>
                            </div>
                            <div class="cqd-meta__cell">
                                <em>点评时间</em>
                                <strong>{{ formatDateTime(detail.FirstSubmitTime) }}</strong>
                            </div>
                            <div class="cqd-meta__cell">
                                <em>最近修改</em>
                                <strong>{{ formatDateTime(detail.LastModifyTime) }}</strong>
                            </div>
                        </div>

                        <div class="cqd-scroll">
                        <template v-if="hasReview">
                            <div class="cqd-score">
                                <div class="cqd-grade" :class="gradeTone(detail.Grade)">
                                    <em>等级</em>
                                    <strong>{{ detail.Grade || '—' }}</strong>
                                </div>
                                <div class="cqd-score__num">
                                    <em>扣分</em>
                                    <strong>{{ formatDeduct(detail.DeductScore) }}</strong>
                                </div>
                                <!-- 开启「要求预设点评项」时才有官方得分；满分 0 表示未打预设项 -->
                                <div class="cqd-score__num" v-if="hasOfficialScore">
                                    <em>预设项得分</em>
                                    <strong>{{ formatScoreNum(detail.OfficialScore) }}</strong>
                                    <small>/ {{ formatScoreNum(detail.OfficialMaxScore) }}</small>
                                </div>
                                <p v-if="detail.Remark" class="cqd-remark" :title="detail.Remark">{{ detail.Remark }}</p>
                            </div>
                            <el-tabs v-model="detailTab" class="cqd-tabs" @tab-click="onDetailTabClick">
                                <el-tab-pane name="items">
                                    <span slot="label">点评明细 <em>{{ detailItemCount }}</em></span>
                                    <div class="cqd-pane">
                                        <div v-if="detailItemCount" class="cqd-pane__table">
                                            <el-table ref="detailItemTable" :data="detail.Items" size="mini" border height="100%">
                                                <el-table-column prop="Category1Name" label="一级" min-width="100" show-overflow-tooltip></el-table-column>
                                                <el-table-column prop="Category2Name" label="二级" min-width="100" show-overflow-tooltip></el-table-column>
                                                <el-table-column prop="ItemName" label="项目" min-width="140" show-overflow-tooltip>
                                                    <template slot-scope="scope">
                                                        {{ scope.row.ItemName }}<span v-if="scope.row.IsCustom" class="cqd-custom">自定义</span>
                                                    </template>
                                                </el-table-column>
                                                <el-table-column label="得分" width="88" align="center">
                                                    <template slot-scope="scope">{{ formatScoreNum(scope.row.Score) }}</template>
                                                </el-table-column>
                                                <el-table-column label="满分" width="88" align="center">
                                                    <template slot-scope="scope">{{ formatScoreNum(scope.row.MaxScore) }}</template>
                                                </el-table-column>
                                            </el-table>
                                        </div>
                                        <div v-else class="cqd-inline-empty">暂无明细</div>
                                    </div>
                                </el-tab-pane>
                                <el-tab-pane name="students">
                                    <span slot="label">学生名单 <em>{{ detailStuCount }}</em></span>
                                    <div class="cqd-pane">
                                        <div class="cqd-pane__toolbar">
                                            <div class="cqd-pane__hint">首次提交时冻结，之后改评不换人</div>
                                            <el-input
                                                v-if="detailStuCount"
                                                v-model="detailStuKeyword"
                                                size="mini"
                                                clearable
                                                prefix-icon="el-icon-search"
                                                placeholder="姓名 / 学号"
                                                class="cqd-stu-search"
                                            ></el-input>
                                        </div>
                                        <div v-if="detailStuCount" class="cqd-pane__table">
                                            <el-table ref="detailStuTable" :data="filteredDetailStudents" size="mini" border height="100%">
                                                <el-table-column label="号次" width="72" align="center">
                                                    <template slot-scope="scope">
                                                        {{ scope.row.SeatNumber > 0 ? scope.row.SeatNumber : '-' }}
                                                    </template>
                                                </el-table-column>
                                                <el-table-column prop="StuName" label="姓名" min-width="100" show-overflow-tooltip></el-table-column>
                                                <el-table-column prop="StuCode" label="学号" min-width="120" show-overflow-tooltip></el-table-column>
                                                <el-table-column prop="ClassName" label="班级" min-width="140" show-overflow-tooltip></el-table-column>
                                            </el-table>
                                        </div>
                                        <div v-else class="cqd-inline-empty">暂无冻结名单</div>
                                    </div>
                                </el-tab-pane>
                            </el-tabs>
                        </template>
                        <div v-else class="cqd-pending">
                            <i class="el-icon-edit-outline"></i>
                            <strong>该课堂尚未提交点评</strong>
                            <em>提交后将在这里看到等级、得分明细和冻结学生名单</em>
                        </div>
                        </div>
                    </template>
                    <div v-else-if="!detailLoading" class="cq-detail-empty">
                        <i class="el-icon-document"></i>
                        <p>该课堂尚未提交点评</p>
                    </div>
                </div>
            </el-dialog>
        </section>
    </section>
</template>

<script>
import {
    getClassroomQualityRecords,
    getClassroomQualityClassRanking,
    getClassroomQualityClassDeductSummary,
    exportClassroomQualityRecords,
    exportClassroomQualityClassDeductSummary,
    getClassroomQualityRecordDetail,
    deleteClassroomQualityReview,
    getClassroomQualitySpecialTimes,
} from '../../api/modules/classroom-quality';
import { resolveActiveSchoolId } from '../../utils/deptCascaderProps';
import DepartmentPane from '../../components/DepartmentPane';
import ToolbarFilterBar from '../../components/ToolbarFilterBar';

function formatDate(d) {
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
}
function todayStr() {
    return formatDate(new Date());
}
function addDays(base, days) {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return formatDate(d);
}
/** 本周一～周日（本地时区）。最后更新：2026-10-31 */
function currentWeekRange() {
    const now = new Date();
    const day = now.getDay(); // 0=周日
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    return [formatDate(monday), formatDate(sunday)];
}

const MAX_QUERY_DAYS = 31;

export default {
    name: 'ClassroomQualityReviewBoard',
    components: { DepartmentPane, ToolbarFilterBar },
    props: {
        courseSource: { type: String, required: true },
    },
    data() {
        return {
            schoolId: 0,
            classId: null,
            dateRange: currentWeekRange(),
            rangePickMin: null,
            filters: { campusId: null, specialCourseTimeId: null, status: '' },
            specialTimes: [],
            activeTab: 'records',
            listLoading: false,
            rankLoading: false,
            deductLoading: false,
            exporting: false,
            rows: [],
            ranking: [],
            deductSummary: [],
            summary: {},
            page: 1,
            pageSize: 20,
            total: 0,
            detailStuKeyword: '',
            detailVisible: false,
            detailLoading: false,
            detail: null,
            detailTab: 'items',
        };
    },
    computed: {
        datePickerOptions() {
            const vm = this;
            return {
                onPick({ maxDate, minDate }) {
                    vm.rangePickMin = minDate;
                    if (maxDate) vm.rangePickMin = null;
                },
                disabledDate(time) {
                    if (!vm.rangePickMin) return false;
                    const min = vm.rangePickMin.getTime();
                    const span = (MAX_QUERY_DAYS - 1) * 24 * 3600 * 1000;
                    const t = time.getTime();
                    return t < min - span || t > min + span;
                },
            };
        },
        hasReview() {
            const id = this.detail && this.detail.ReviewId;
            const key = id != null && id !== '' ? String(id).trim() : '';
            return !!key && key !== '0';
        },
        detailItemCount() {
            return ((this.detail && this.detail.Items) || []).length;
        },
        detailStuCount() {
            return ((this.detail && this.detail.Students) || []).length;
        },
        hasOfficialScore() {
            const max = this.detail && this.detail.OfficialMaxScore;
            return max != null && Number(max) > 0;
        },
        detailSessionTime() {
            const session = this.detail && this.detail.Session;
            if (!session) return '';
            const start = this.formatClock(session.StartAt);
            const end = this.formatClock(session.EndAt);
            if (!start && !end) return '';
            return `${start || '--:--'} ~ ${end || '--:--'}`;
        },
        filteredDetailStudents() {
            const list = [...((this.detail && this.detail.Students) || [])].sort((a, b) => {
                const sa = Number(a.SeatNumber) > 0 ? Number(a.SeatNumber) : Number.MAX_SAFE_INTEGER;
                const sb = Number(b.SeatNumber) > 0 ? Number(b.SeatNumber) : Number.MAX_SAFE_INTEGER;
                if (sa !== sb) return sa - sb;
                return String(a.StuName || '').localeCompare(String(b.StuName || ''), 'zh-CN');
            });
            const key = (this.detailStuKeyword || '').trim().toLowerCase();
            if (!key) return list;
            return list.filter(s => {
                const name = String(s.StuName || '').toLowerCase();
                const code = String(s.StuCode || '').toLowerCase();
                const seat = String(s.SeatNumber || '');
                return name.indexOf(key) >= 0 || code.indexOf(key) >= 0 || seat.indexOf(key) >= 0;
            });
        },
    },
    methods: {
        formatDate(val) {
            if (!val) return '-';
            return String(val).substring(0, 10);
        },
        formatDateTime(val) {
            if (!val) return '-';
            return String(val).replace('T', ' ').substring(0, 19);
        },
        formatClock(val) {
            if (!val) return '';
            const raw = String(val).replace('T', ' ');
            if (raw.length >= 16) return raw.substring(11, 16);
            if (/^\d{2}:\d{2}/.test(raw)) return raw.substring(0, 5);
            return '';
        },
        formatRate(val) {
            if (val == null || val === '') return '-';
            return `${val}%`;
        },
        formatScoreNum(val) {
            if (val == null || val === '') return '0';
            return val;
        },
        formatDeduct(val) {
            if (val == null || val === '') return '-';
            const n = Number(val);
            if (Number.isNaN(n)) return '-';
            return n;
        },
        gradeTone(grade) {
            if (grade === '优') return 'is-excellent';
            if (grade === '良') return 'is-good';
            if (grade === '合格') return 'is-pass';
            if (grade === '不合格') return 'is-fail';
            return 'is-custom';
        },
        statusChipClass(status) {
            const map = {
                Pending: 'is-pending',
                WaitingMakeup: 'is-waiting',
                ReviewedOnTime: 'is-done',
                ReviewedMakeup: 'is-makeup',
                Upcoming: 'is-upcoming',
                Expired: 'is-expired'
            };
            return map[status] || 'is-upcoming';
        },
        formatStatusText(statusText, status) {
            const map = {
                ReviewedOnTime: '已点评',
                ReviewedMakeup: '已补评',
                WaitingMakeup: '待补评',
                Pending: '待点评',
                Upcoming: '未到开放',
                Expired: '已过期',
                补点评: '已补评',
                已点评: '已点评',
                待补评: '待补评',
                待点评: '待点评',
                已过期: '已过期',
                未到开放时间: '未到开放'
            };
            return map[statusText] || map[status] || statusText || status || '—';
        },
        resolveSchoolId() {
            // 学校 Id 用字符串，避免大整型精度丢失。最后修改：2026-10-18
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return String(fromStore);
            if (this.schoolId) return String(this.schoolId);
            return '';
        },
        /**
         * 组织树节点 Id 转字符串；无效则空串。最后修改：2026-10-18
         */
        toDeptId(raw) {
            if (raw == null || raw === '') return '';
            const key = String(raw).trim();
            if (!key || key === '0') return '';
            return key;
        },
        treeNodeClick(data) {
            const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
            const id = this.toDeptId(data.value != null ? data.value : data.Id);
            if (orgType === 'school' || orgType === 'affiliateschool') {
                this.schoolId = id;
                this.classId = null;
                this.filters.campusId = null;
            } else if (orgType === 'campus') {
                this.filters.campusId = id || null;
                this.classId = null;
            } else if (orgType === 'class' || orgType === 'period' || orgType === 'stage' || orgType === 'grade') {
                this.classId = id || null;
                this.filters.campusId = null;
            } else if (id && orgType !== 'platform' && orgType !== 'allschools') {
                this.classId = id;
                this.filters.campusId = null;
            }
            if (!this.schoolId) this.schoolId = this.resolveSchoolId();
            if (this.courseSource === 'Special') {
                const timeKey = `${this.resolveSchoolId()}|${this.filters.campusId || ''}`;
                if (this._timeKey !== timeKey) {
                    this._timeKey = timeKey;
                    this.loadSpecialTimes();
                }
            }
            const selKey = `${this.resolveSchoolId()}|${this.classId || ''}|${this.filters.campusId || ''}`;
            if (this._treeSelKey === selKey) return;
            this._treeSelKey = selKey;
            this.handleQuery();
        },
        formatSpecialTimeLabel(row) {
            const name = (row && row.TimeName) || '';
            const campus = row && row.CampusId
                ? (row.CampusName || '校区')
                : '全校';
            return name ? `${name}（${campus}）` : campus;
        },
        loadSpecialTimes() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId || this.courseSource !== 'Special') return;
            getClassroomQualitySpecialTimes({
                schoolId,
                campusId: this.filters.campusId || undefined,
            }).then(res => {
                this.specialTimes = (res.data && res.data.response) || [];
                const ids = this.specialTimes.map(t => t.Id);
                if (this.filters.specialCourseTimeId && ids.indexOf(this.filters.specialCourseTimeId) < 0) {
                    this.filters.specialCourseTimeId = null;
                }
            });
        },
        handleQuery() {
            this.ensureDateRange();
            this.page = 1;
            this.loadRecords();
            if (this.activeTab === 'stats') this.loadRanking();
            if (this.activeTab === 'deduct') this.loadDeductSummary();
        },
        /**
         * 点击汇总数字联动状态筛选；再点一次同一项则清空。创建于 2026-10-31
         */
        filterByStat(status) {
            const next = status || '';
            if ((this.filters.status || '') === next) {
                this.filters.status = '';
            } else {
                this.filters.status = next;
            }
            this.activeTab = 'records';
            this.handleQuery();
        },
        ensureDateRange() {
            if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
                this.dateRange = currentWeekRange();
                return;
            }
            const start = new Date(`${this.dateRange[0]}T00:00:00`);
            const end = new Date(`${this.dateRange[1]}T00:00:00`);
            const days = Math.round((end - start) / (24 * 3600 * 1000)) + 1;
            if (days > MAX_QUERY_DAYS) {
                this.dateRange = [this.dateRange[0], addDays(start, MAX_QUERY_DAYS - 1)];
                this.$message.warning(`查询区间最多 ${MAX_QUERY_DAYS} 天`);
            }
        },
        onTabClick() {
            if (this.activeTab === 'stats') this.loadRanking();
            if (this.activeTab === 'deduct') this.loadDeductSummary();
        },
        onDetailTabClick() {
            this.$nextTick(() => {
                const tables = [this.$refs.detailItemTable, this.$refs.detailStuTable];
                tables.forEach(t => {
                    if (t && typeof t.doLayout === 'function') t.doLayout();
                });
            });
        },
        buildCommonParams() {
            const schoolId = this.resolveSchoolId();
            return {
                schoolId,
                courseSource: this.courseSource,
                startDate: this.dateRange && this.dateRange[0],
                endDate: this.dateRange && this.dateRange[1],
                classId: this.classId || undefined,
                campusId: this.filters.campusId || undefined,
                specialCourseTimeId: this.filters.specialCourseTimeId || undefined,
                status: this.filters.status || undefined,
            };
        },
        loadRecords() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) return;
            this.listLoading = true;
            getClassroomQualityRecords({
                ...this.buildCommonParams(),
                page: this.page,
                pageSize: this.pageSize,
            }).then(res => {
                const data = (res.data && res.data.response) || {};
                this.summary = data.summary || {};
                const page = data.page || {};
                this.rows = page.data || [];
                this.total = page.dataCount || 0;
            }).finally(() => { this.listLoading = false; });
        },
        loadRanking() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) return;
            this.rankLoading = true;
            getClassroomQualityClassRanking(this.buildCommonParams()).then(res => {
                this.ranking = (res.data && res.data.response) || [];
            }).finally(() => { this.rankLoading = false; });
        },
        loadDeductSummary() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) return;
            this.deductLoading = true;
            getClassroomQualityClassDeductSummary(this.buildCommonParams()).then(res => {
                this.deductSummary = (res.data && res.data.response) || [];
            }).finally(() => { this.deductLoading = false; });
        },
        downloadBlob(data, fileName) {
            const blob = new Blob([data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },
        async handleExport() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) {
                this.$message.warning('请先选择学校');
                return;
            }
            this.ensureDateRange();
            const params = this.buildCommonParams();
            const start = (params.startDate || '').replace(/-/g, '');
            const end = (params.endDate || '').replace(/-/g, '');
            const range = start && end ? `${start}_${end}` : todayStr().replace(/-/g, '');
            this.exporting = true;
            try {
                if (this.activeTab === 'deduct') {
                    const response = await exportClassroomQualityClassDeductSummary(params);
                    if (!response.data) {
                        this.$message.error('导出数据为空');
                        return;
                    }
                    this.downloadBlob(response.data, `班级扣分汇总-${range}.xlsx`);
                } else {
                    const response = await exportClassroomQualityRecords(params);
                    if (!response.data) {
                        this.$message.error('导出数据为空');
                        return;
                    }
                    this.downloadBlob(response.data, `课堂质量点评明细-${range}.xlsx`);
                }
            } catch (e) {
                this.$message.error('导出失败');
            } finally {
                this.exporting = false;
            }
        },
        handleCurrentChange(val) {
            this.page = val;
            this.loadRecords();
        },
        handleView(row) {
            if (!row || !row.TaskId) {
                this.$message.info('该课堂尚未提交点评');
                return;
            }
            this.detail = null;
            this.detailVisible = true;
            this.detailLoading = true;
            this.detailTab = 'items';
            this.detailStuKeyword = '';
            getClassroomQualityRecordDetail({ taskId: row.TaskId }).then(res => {
                this.detail = (res.data && res.data.response) || null;
            }).finally(() => { this.detailLoading = false; });
        },
        onDetailClosed() {
            this.detail = null;
            this.detailLoading = false;
            this.detailTab = 'items';
            this.detailStuKeyword = '';
        },
        handleDel(row) {
            this.$confirm('删除后该课堂可重新提交点评，学生名单将按当时在籍重建。确认删除？', '提示', { type: 'warning' }).then(() => {
                deleteClassroomQualityReview({ reviewId: row.ReviewId }).then(res => {
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '已删除');
                        this.loadRecords();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '删除失败');
                    }
                });
            }).catch(() => {});
        },
    },
};
</script>

<style scoped>
.stat-summary {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}
.stat-summary-item {
    flex: 1;
    min-width: 90px;
    background: #f8fafc;
    border-radius: 8px;
    padding: 8px 10px;
    border: 1px solid transparent;
    transition: border-color 0.15s ease, background 0.15s ease;
}
.stat-summary-item.is-clickable {
    cursor: pointer;
}
.stat-summary-item.is-clickable:hover {
    border-color: #bfdbfe;
    background: #eff6ff;
}
.stat-summary-item.is-active {
    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.35);
}
.stat-summary-label { display: block; color: #909399; font-size: 12px; }
.stat-summary-value { font-size: 18px; font-weight: 700; color: #303133; font-variant-numeric: tabular-nums; }
.stat-summary-item.is-tone-due { background: #eff6ff; }
.stat-summary-item.is-tone-due .stat-summary-value { color: #2563eb; }
.stat-summary-item.is-tone-due.is-active { border-color: #93c5fd; box-shadow: none; }
.stat-summary-item.is-tone-done { background: #f0fdf4; }
.stat-summary-item.is-tone-done .stat-summary-value { color: #16a34a; }
.stat-summary-item.is-tone-done.is-active { border-color: #86efac; box-shadow: none; }
.stat-summary-item.is-tone-ontime { background: #ecfdf5; }
.stat-summary-item.is-tone-ontime .stat-summary-value { color: #059669; }
.stat-summary-item.is-tone-ontime.is-active { border-color: #6ee7b7; box-shadow: none; }
.stat-summary-item.is-tone-makeup { background: #eef2ff; }
.stat-summary-item.is-tone-makeup .stat-summary-value { color: #4f46e5; }
.stat-summary-item.is-tone-makeup.is-active { border-color: #a5b4fc; box-shadow: none; }
.stat-summary-item.is-tone-miss { background: #fff7ed; }
.stat-summary-item.is-tone-miss .stat-summary-value { color: #ea580c; }
.stat-summary-item.is-tone-miss.is-active { border-color: #fdba74; box-shadow: none; }
.stat-summary-item.is-tone-expired { background: #f8fafc; }
.stat-summary-item.is-tone-expired .stat-summary-value { color: #64748b; }
.stat-summary-item.is-tone-expired.is-active { border-color: #cbd5e1; box-shadow: none; }
.stat-summary-item.is-tone-rate { background: #f0f9ff; }
.stat-summary-item.is-tone-rate .stat-summary-value { color: #0284c7; }
.stat-summary-item.is-tone-history { background: #f8fafc; }
.stat-summary-item.is-tone-history .stat-summary-value { color: #64748b; }
.stat-summary-item.is-tone-history.is-active { border-color: #cbd5e1; box-shadow: none; }
.muted { color: #909399; }
.cq-num {
    display: inline-block;
    min-width: 28px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}
.cq-num.is-due { color: #2563eb; }
.cq-num.is-done { color: #16a34a; }
.cq-num.is-miss { color: #ea580c; }
.cq-num.is-miss.is-zero { color: #94a3b8; font-weight: 600; }
.cq-num.is-deduct-soft { color: #64748b; font-weight: 600; }
.cq-num.is-deduct-warn { color: #d97706; }
.cq-num.is-deduct-total {
    color: #dc2626;
    padding: 2px 8px;
    border-radius: 999px;
    background: #fef2f2;
}
.cq-deduct-table >>> .el-table__header th {
    background: #f8fafc;
}
.cq-cell-stack {
    line-height: 1.35;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.cq-cell-stack strong {
    font-size: 13px;
    font-weight: 600;
    color: #1f2a44;
}
.cq-cell-stack span {
    font-size: 13px;
    color: #303133;
}
.cq-cell-stack em {
    font-style: normal;
    font-size: 12px;
    color: #909399;
}
.cq-cell-stack--center {
    align-items: center;
}
.cq-status-tags {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
}
.cq-status-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0.02em;
}
.cq-status-chip.is-pending {
    background: #fef3c7;
    color: #b45309;
}
.cq-status-chip.is-waiting {
    background: #ffedd5;
    color: #c2410c;
}
.cq-status-chip.is-done {
    background: #dcfce7;
    color: #15803d;
}
.cq-status-chip.is-makeup {
    background: #e0e7ff;
    color: #4338ca;
}
.cq-status-chip.is-upcoming {
    background: #f1f5f9;
    color: #64748b;
}
.cq-status-chip.is-expired {
    background: #f1f5f9;
    color: #94a3b8;
}
.cq-status-chip.is-history {
    background: #e2e8f0;
    color: #475569;
}
.cq-result-pill {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
}
.cq-result-pill i {
    font-style: normal;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.85;
}
.cq-result-pill.is-excellent {
    background: #ecfdf5;
    color: #047857;
}
.cq-result-pill.is-good {
    background: #eff6ff;
    color: #1d4ed8;
}
.cq-result-pill.is-pass {
    background: #fffbeb;
    color: #b45309;
}
.cq-result-pill.is-fail {
    background: #fef2f2;
    color: #b91c1c;
}
.cq-result-pill.is-custom {
    background: #f1f5f9;
    color: #475569;
}
.cq-period {
    line-height: 1.25;
}
.cq-period strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #1f2a44;
}
.cq-period em {
    display: block;
    margin-top: 2px;
    font-style: normal;
    font-size: 12px;
    color: #909399;
}
.cq-detail-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    overflow: hidden;
}
.cqd-head {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.cqd-kicker {
    margin: 0 0 4px;
    font-size: 12px;
    letter-spacing: 0.04em;
    color: #94a3b8;
}
.cqd-head h3 {
    margin: 0;
    font-size: 20px;
    line-height: 1.3;
    color: #0f172a;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
}
.cqd-head h3 .cqd-time {
    font-style: normal;
    font-size: 15px;
    font-weight: 600;
    color: #2563eb;
    letter-spacing: 0.02em;
}
.cqd-sub {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
}
.cqd-sub span + span::before {
    content: '·';
    margin-right: 8px;
    color: #cbd5e1;
}
.cqd-meta {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}
.cqd-meta__cell {
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 10px;
    padding: 10px 12px;
}
.cqd-meta__cell em {
    display: block;
    font-style: normal;
    font-size: 12px;
    color: #94a3b8;
}
.cqd-meta__cell strong {
    display: block;
    margin-top: 4px;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    word-break: break-all;
}
.cqd-scroll {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
}
.cqd-score {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 36px;
}
.cqd-grade,
.cqd-score__num {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    flex-shrink: 0;
    border-radius: 8px;
    padding: 6px 10px;
    line-height: 1.2;
}
.cqd-grade {
    background: #eff6ff;
    color: #1d4ed8;
}
.cqd-grade.is-excellent { background: #ecfdf5; color: #047857; }
.cqd-grade.is-good { background: #eff6ff; color: #1d4ed8; }
.cqd-grade.is-pass { background: #fff7ed; color: #c2410c; }
.cqd-grade.is-fail { background: #fef2f2; color: #b91c1c; }
.cqd-grade em,
.cqd-score__num em {
    font-style: normal;
    font-size: 12px;
    opacity: 0.75;
}
.cqd-grade strong {
    font-size: 16px;
    font-weight: 700;
}
.cqd-score__num {
    background: #f8fafc;
    border: 1px solid #eef2f6;
    color: #0f172a;
}
.cqd-score__num strong {
    font-size: 16px;
    font-weight: 700;
}
.cqd-score__num small {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
}
.cqd-remark {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    padding: 6px 10px;
    background: #fffbeb;
    border-radius: 8px;
    color: #92400e;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.cqd-tabs {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.cqd-tabs >>> .el-tabs__header {
    flex-shrink: 0;
    margin: 0 0 8px;
}
.cqd-tabs >>> .el-tabs__item {
    height: 36px;
    line-height: 36px;
    font-weight: 600;
}
.cqd-tabs >>> .el-tabs__item em {
    margin-left: 4px;
    font-style: normal;
    font-weight: 500;
    color: #94a3b8;
}
.cqd-tabs >>> .el-tabs__item.is-active em {
    color: #409eff;
}
.cqd-tabs >>> .el-tabs__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}
.cqd-tabs >>> .el-tab-pane {
    height: 100%;
}
.cqd-pane {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.cqd-pane__toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 8px;
}
.cqd-pane__hint {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 12px;
    color: #94a3b8;
}
.cqd-stu-search {
    width: 200px;
    flex-shrink: 0;
}
.cqd-pane__table {
    flex: 1 1 auto;
    min-height: 0;
}
.cqd-custom {
    margin-left: 6px;
    font-size: 12px;
    color: #2563eb;
}
.cqd-inline-empty,
.cqd-pending,
.cq-detail-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #94a3b8;
    text-align: center;
}
.cqd-inline-empty {
    flex: 1 1 auto;
    min-height: 88px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
}
.cqd-pending {
    flex: 1 1 auto;
    min-height: 180px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border: 1px dashed #dbe3ee;
    border-radius: 14px;
}
.cqd-pending i,
.cq-detail-empty i {
    font-size: 28px;
    color: #cbd5e1;
}
.cqd-pending strong {
    color: #334155;
    font-size: 15px;
}
.cqd-pending em {
    font-style: normal;
    font-size: 13px;
}
.cq-detail-empty {
    flex: 1 1 auto;
}
.cq-detail-empty p {
    margin: 0;
    font-size: 14px;
}
@media (max-width: 720px) {
    .cqd-meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>

<style>
.el-dialog.cq-detail-dialog:not(.is-fullscreen) {
    display: flex;
    flex-direction: column;
    height: min(760px, calc(100vh - 8vh));
    height: min(760px, calc(100dvh - 8vh));
}
.el-dialog.cq-detail-dialog > .el-dialog__body {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
</style>
